(() => {
  if (customElements.get('slofe-bowl-3d')) return;
  const colors = {WH:'#f0f0ed',BK:'#222429',GY:'#707680',LB:'#4da4ca',BL:'#234aa0',TQ:'#138f98',LG:'#80ad46',GN:'#347044',YL:'#e7b922',OR:'#e57223',RD:'#c82d3d',PK:'#d77eaa',PC:'#e5a06f',PU:'#8544ae',MG:'#cb0188'};
  let featurePromise;
  const loadViewer = () => {
    if (customElements.get('model-viewer')) return Promise.resolve();
    if (!featurePromise) {
      featurePromise = new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('Viewer timeout')), 20000);
        const done = () => { clearTimeout(timer); resolve(); };
        if (!window.Shopify?.loadFeatures) { clearTimeout(timer); reject(new Error('Viewer unavailable')); return; }
        Shopify.loadFeatures([{name:'model-viewer',version:'1.12',onLoad:(error) => {
          if (error && !customElements.get('model-viewer')) { clearTimeout(timer); reject(error); return; }
          customElements.whenDefined('model-viewer').then(done);
        }}]);
      }).catch((error) => { featurePromise = null; throw error; });
    }
    return featurePromise;
  };
  class BowlPreview extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abort = new AbortController();
      const options = {signal:this.abort.signal};
      this.config = JSON.parse(this.querySelector('[data-bowl3d-config]').textContent);
      this.stage = this.querySelector('.bowl3d-stage');
      this.start = this.querySelector('.bowl3d-start');
      this.error = this.querySelector('.bowl3d-error');
      this.cache = new Map();
      this.sequence = 0;
      this.current = this.config.variants.find(v => String(v.id) === String(this.config.initialId)) || this.config.variants[0];
      this.querySelector('.bowl3d-shape-list').replaceChildren();
      this.querySelector('.bowl3d-color-list').replaceChildren();
      const forms = new Map(), shades = new Map();
      this.config.variants.forEach(v => {
        forms.set(v.sku.split('-')[0],v.options[this.config.shapeIndex ?? 1]);
        shades.set(v.sku.split('-')[2],v.options[this.config.colorIndex ?? 0]);
      });
      forms.forEach((name, id) => {
        const button = this.button(name);
        button.dataset.shape = id;
        button.addEventListener('click',() => this.choose(id,this.current.sku.split('-')[2]),options);
        this.querySelector('.bowl3d-shape-list').append(button);
      });
      shades.forEach((name, id) => {
        const button = this.button('');
        button.dataset.color = id;
        button.title = name;
        button.setAttribute('aria-label',name);
        button.style.setProperty('--bowl-color',colors[id]);
        button.style.setProperty('--bowl-ink',['BK','BL','TQ','GN','PU','RD','MG'].includes(id) ? '#fff' : '#24323b');
        button.append(document.createElement('span'));
        button.addEventListener('click',() => this.choose(this.current.sku.split('-')[0],id),options);
        this.querySelector('.bowl3d-color-list').append(button);
      });
      this.start.addEventListener('click',() => this.activate(),options);
      this.querySelectorAll('[data-view]').forEach(button => button.addEventListener('click',() => {
        if (!this.viewer) return;
        this.viewer.cameraOrbit = button.dataset.view === 'top' ? '0deg 0deg 105%' : '25deg 52deg 105%';
        this.viewer.fieldOfView = '30deg';
      },options));
      document.addEventListener('change',event => {
        const main = this.mainProduct();
        if (!main?.contains(event.target)) return;
        if (event.isTrusted) delete this.dataset.syncing;
        if (event.target.name === 'id') {
          this.dataset.purchaseVariant = String(event.target.value);
          this.follow(event.target.value);
        }
        else if (event.target.closest('variant-selects')) this.followOptions();
      },options);
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange,({data}) => {
          if (String(data.sectionId) === String(this.mainProduct()?.dataset.section)) {
            this.follow(data.variant?.id);
            this.dataset.purchaseVariant = String(data.variant?.id || '');
            queueMicrotask(() => { delete this.dataset.syncing; });
          }
        });
      }
      this.refresh();
    }
    button(text) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = text;
      button.setAttribute('aria-pressed','false');
      return button;
    }
    mainProduct() {
      return document.querySelector('product-info[id^="MainProduct"]');
    }
    follow(id) {
      const variant = this.config.variants.find(v => String(v.id) === String(id));
      if (variant && variant.id !== this.current.id) this.select(variant);
    }
    followOptions() {
      const groups = this.mainProduct()?.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown');
      if (!groups?.length) return;
      const values = Array.from(groups).map(group => group.querySelector('input:checked')?.value || group.querySelector('select')?.value);
      const variant = this.config.variants.find(v => v.options.every((value,index) => value === values[index]));
      if (variant) this.follow(variant.id);
    }
    matchesExtraOptions(variant) {
      const shapeIndex = this.config.shapeIndex ?? 1;
      const colorIndex = this.config.colorIndex ?? 0;
      return variant.options.every((value,index) => index === shapeIndex || index === colorIndex || value === this.current.options[index]);
    }
    choose(shape, color) {
      const variant = this.config.variants.find(v => v.sku.split('-')[0] === shape && v.sku.split('-')[2] === color && this.matchesExtraOptions(v));
      if (!variant) return;
      const previous = this.current;
      this.select(variant);
      const picker = this.mainProduct()?.querySelector('variant-selects');
      if (picker) {
        const groups = picker.querySelectorAll('fieldset, .product-form__input--dropdown');
        const changedIndex = previous.options.findIndex((value,index) => value !== variant.options[index]);
        if (changedIndex >= 0) {
          const group = groups[changedIndex];
          const input = Array.from(group?.querySelectorAll('input[type=radio]') || []).find(node => node.value === variant.options[changedIndex]);
          const select = group?.querySelector('select');
          if (input) {
            this.preserveFocus();
            input.checked = true;
            input.dispatchEvent(new Event('change',{bubbles:true}));
          } else if (select && Array.from(select.options).some(option => option.value === variant.options[changedIndex])) {
            this.preserveFocus();
            select.value = variant.options[changedIndex];
            select.dispatchEvent(new Event('change',{bubbles:true}));
          } else this.showError(this.config.messages.sync_error);
        }
      }
      if (!this.active) this.activate();
    }
    select(variant) {
      this.current = variant;
      this.refresh();
      if (this.active) this.loadModel();
    }
    preserveFocus() {
      this.dataset.syncing = '';
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => { delete this.dataset.syncing; },15000);
    }
    refresh() {
      const [shape,,color] = this.current.sku.split('-');
      this.querySelectorAll('[data-shape]').forEach(button => {
        button.setAttribute('aria-pressed',String(button.dataset.shape === shape));
        button.disabled = !this.config.variants.some(v => v.sku.startsWith(button.dataset.shape + '-') && v.sku.split('-')[2] === color && this.matchesExtraOptions(v));
      });
      this.querySelectorAll('[data-color]').forEach(button => {
        button.setAttribute('aria-pressed',String(button.dataset.color === color));
        button.disabled = !this.config.variants.some(v => v.sku.startsWith(shape + '-') && v.sku.split('-')[2] === button.dataset.color && this.matchesExtraOptions(v));
      });
      this.querySelector('.bowl3d-color-name').textContent = this.current.options[this.config.colorIndex ?? 0];
      const extraOptions = this.current.options.filter((value,index) => index !== (this.config.shapeIndex ?? 1) && index !== (this.config.colorIndex ?? 0));
      this.querySelector('.bowl3d-selection').textContent = [this.current.options[this.config.shapeIndex ?? 1],this.current.options[this.config.colorIndex ?? 0],...extraOptions].join(' · ') + (this.current.available ? '' : ' · ' + this.config.messages.unavailable);
      if (this.viewer) this.viewer.alt = this.config.messages.alt + ': ' + this.current.options.join(' / ');
    }
    async activate() {
      this.active = true;
      this.start.disabled = true;
      this.start.textContent = this.config.messages.loading;
      this.error.hidden = true;
      this.stage.setAttribute('aria-busy','true');
      try {
        await loadViewer();
        if (!this.isConnected) return;
        if (!this.viewer) {
          this.viewer = document.createElement('model-viewer');
          this.viewer.setAttribute('camera-controls','');
          this.viewer.setAttribute('loading','eager');
          this.viewer.setAttribute('reveal','auto');
          this.viewer.setAttribute('disable-pan','');
          this.viewer.setAttribute('touch-action','pan-y');
          this.viewer.setAttribute('interaction-prompt','none');
          this.viewer.setAttribute('shadow-intensity','0.65');
          this.viewer.setAttribute('shadow-softness','0.85');
          this.viewer.setAttribute('exposure','0.85');
          this.viewer.setAttribute('camera-orbit','25deg 52deg 105%');
          this.viewer.setAttribute('field-of-view','30deg');
          this.viewer.setAttribute('min-camera-orbit','auto auto 45%');
          this.viewer.setAttribute('max-camera-orbit','auto auto 250%');
          this.viewer.addEventListener('load',() => {
            if (!this.expectedUrl || this.viewer.getAttribute('src') !== this.expectedUrl || this.loadingShape !== this.current.sku.split('-')[0]) return;
            this.completeModel();
          },{signal:this.abort.signal});
          this.viewer.addEventListener('error',() => this.fail(),{signal:this.abort.signal});
          this.querySelector('.bowl3d-canvas').append(this.viewer);
        }
        this.refresh();
        this.loadingShape = null;
        await this.loadModel();
      } catch (error) {
        this.fail();
      }
    }
    async modelUrl(shape) {
      if (!this.cache.has(shape)) {
        const promise = fetch(this.config.models[shape],{signal:this.abort.signal}).then(response => {
          if (!response.ok) throw new Error('Model unavailable');
          return response.json();
        }).then(pack => {
          if (pack.format !== 'glb-base64') throw new Error('Invalid model');
          const bytes = Uint8Array.from(atob(pack.data),character => character.charCodeAt(0));
          if (!this.isConnected) throw new Error('Detached');
          return URL.createObjectURL(new Blob([bytes],{type:'model/gltf-binary'}));
        }).catch(error => { this.cache.delete(shape); throw error; });
        this.cache.set(shape,promise);
      }
      return this.cache.get(shape);
    }
    async loadModel() {
      if (!this.viewer) return;
      const shape = this.current.sku.split('-')[0];
      if (this.loadingShape === shape) { this.applyColor(); return; }
      const sequence = ++this.sequence;
      this.loadingShape = shape;
      this.expectedUrl = null;
      this.error.hidden = true;
      this.stage.setAttribute('aria-busy','true');
      clearTimeout(this.loadTimer);
      this.loadTimer = setTimeout(() => { if (sequence === this.sequence) this.fail(); },25000);
      try {
        const url = await this.modelUrl(shape);
        if (sequence !== this.sequence || !this.isConnected) return;
        this.expectedUrl = url;
        const sameModel = this.viewer.getAttribute('src') === url && this.viewer.loaded;
        this.viewer.setAttribute('src',url);
        if (sameModel) this.completeModel();
      } catch (error) {
        if (sequence === this.sequence && this.isConnected) this.fail();
      }
    }
    completeModel() {
      this.displayedShape = this.loadingShape;
      this.applyColor();
      this.stage.dataset.loaded = '';
      this.stage.setAttribute('aria-busy','false');
      this.start.hidden = true;
      this.querySelector('.bowl3d-tools').hidden = false;
      clearTimeout(this.loadTimer);
    }
    applyColor() {
      if (!this.viewer?.model || this.displayedShape !== this.current.sku.split('-')[0]) return;
      const hex = colors[this.current.sku.split('-')[2]];
      if (!hex) return;
      const rgb = [1,3,5].map(offset => parseInt(hex.slice(offset,offset+2),16)/255).map(value => value <= .04045 ? value/12.92 : ((value+.055)/1.055)**2.4);
      this.viewer.model.materials.forEach(material => {
        material.pbrMetallicRoughness.setBaseColorFactor([...rgb,1]);
        material.pbrMetallicRoughness.setMetallicFactor(0);
        material.pbrMetallicRoughness.setRoughnessFactor(.62);
      });
      this.dataset.renderedVariant = String(this.current.id);
    }
    showError(text) {
      this.error.textContent = text;
      this.error.hidden = false;
    }
    fail() {
      clearTimeout(this.loadTimer);
      this.loadingShape = null;
      this.stage.setAttribute('aria-busy','false');
      this.start.hidden = false;
      this.start.disabled = false;
      this.start.textContent = this.config.messages.start;
      this.showError(this.config.messages.error);
    }
    disconnectedCallback() {
      this.abort?.abort();
      this.unsubscribe?.();
      clearTimeout(this.loadTimer);
      this.sequence++;
      clearTimeout(this.syncTimer);
      this.cache?.forEach(promise => promise.then(url => URL.revokeObjectURL(url)).catch(() => {}));
      this.initialized = false;
      this.active = false;
      this.viewer?.remove();
      this.viewer = null;
      this.stage?.removeAttribute('data-loaded');
    }
  }
  customElements.define('slofe-bowl-3d',BowlPreview);
})();
